'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/nn-extra'

const DigitDraw = dynamic(() => import('../_components/widgets/NNExtraWidgets').then(m => m.DigitDraw), { ssr: false })

export default function ProjectDigits() {
  return (
    <Lesson>
      <Step>
        <H>Project: read handwriting</H>
        <P>
          Reading handwritten digits was one of the first big successes of neural networks: in the 1990s, Yann LeCun’s networks
          read the numbers on millions of bank cheques. In this project you’ll train a network that reads digits it has never
          seen, find out where it fails, and then test it on your own handwriting.
        </P>
        <OL>
          <li>Train with Adam, mini-batches and dropout, keeping the best weights by validation accuracy.</li>
          <li>Study its mistakes with a confusion matrix.</li>
          <li>Draw digits for a network trained live in your browser.</li>
        </OL>
      </Step>

      <Step gate="digits-train">
        <H>Part 1 · Train, keep the best</H>
        <P>
          2 000 training digits, 300 for validation, 300 held out for the end. The model is a 196 → 128 → 10 network with a
          little dropout. Your job is the bookkeeping that every real training run needs: save the weights from the best epoch
          and restore them at the end.
        </P>
        <Exercise
          id="digits-train" timeoutMs={60000}
          title="snapshot, restore and the training loop"
          {...EX.digitsTrain}
          hints={[
            <>
              <C>{'params.map(p => p.data.slice())'}</C> — <C>slice()</C> makes a copy.
            </>,
            <>
              restore: <C>{'params.forEach((p, i) => p.data.set(s[i]))'}</C>
            </>,
            <>
              In the loop: <C>{'if (acc > best.acc) best = { acc, epoch, weights: snapshot() }'}</C>. After it:{' '}
              <C>restore(best.weights)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="digits-errors">
        <H>Part 2 · Where does it go wrong?</H>
        <P>
          A single accuracy number hides the interesting part. A confusion matrix shows which digits get mistaken for which.
          It’s the first thing to check when a model disappoints you.
        </P>
        <Exercise
          id="digits-errors" timeoutMs={60000}
          title="confusion and worstPair"
          {...EX.digitsErrors}
          hints={[
            <>
              <C>{'truth.forEach((t, i) => C[t][pred[i]]++)'}</C>
            </>,
            <>Loop over every (t, p) with t ≠ p and keep the pair with the largest count.</>,
          ]}
        />
        <Callout kind="plain">
          The confusions usually make sense: 4 and 9, 3 and 5, 7 and 1 really can look alike. When the mistakes are ones a
          person could make too, the model has learned something real. When they’re bizarre, suspect a bug or bad data.
        </Callout>
      </Step>

      <Step>
        <H>Part 3 · Your handwriting</H>
        <P>
          Below, a network like yours trains on 3 000 digits right here in your browser, using hand-written backpropagation on
          plain arrays. When it’s done, draw a digit with your mouse or finger.
        </P>
        <Figure n="7.12" wide caption="Draw a digit from 0 to 9. The small image shows what the network actually sees after your drawing is shrunk to 14×14.">
          <DigitDraw />
        </Figure>
        <Callout kind="try">
          Try to fool it. Draw a 7 with a crossbar, a 1 with a flag, a very slanted 4, a tiny 3 in the corner. Which changes
          break it? The network has only seen what the training data showed it: that’s distribution shift, one of the biggest
          practical problems in AI.
        </Callout>
      </Step>

      <Step>
        <H>Module 7 complete</H>
        <P>
          You’ve built neural networks from single neurons to deep, regularised, convolutional ones, with gradients you derived
          yourself. Next: language. The same ideas, applied to text, lead all the way to ChatGPT.
        </P>
        <UL>
          <li>Stretch: swap the MLP in Part 1 for the CNN from the convolutions lesson. Does validation accuracy improve?</li>
          <li>Stretch: add data augmentation by shifting each training image one pixel left, right, up or down.</li>
        </UL>
      </Step>
    </Lesson>
  )
}
